/**
 * ProfileEditModal — マイページからの名前・アイコン変更。
 *
 * OnboardingModal で「あとからマイページで変えられます」と案内した先がここなので、
 * **同じ Pop/Light トークン**で揃える（旧ダークテーマからの作り直し・2026-08-11）。
 *
 * 【失敗時の扱い】以前は呼び出し側が `alert()` を出し、モーダルは**成功しても失敗しても閉じて**いた。
 * 失敗したのに閉じると入力が消えてやり直せないので、
 * `onSave` に結果を返してもらい、**失敗ならモーダルを開いたまま**画面内に理由を出す。
 */
import { FC, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useImageUpload } from '@/hooks/useImageUpload';
import { COPY } from '../lib/copyDictionary';
import { useGamenics, GamenicsEvent } from '../hooks/useGamenics';

interface ProfileEditModalProps {
    initialName: string;
    initialIcon: string;
    onClose: () => void;
    /** 保存の結果を返す。失敗理由があればモーダル内に表示する（alert を使わない） */
    onSave: (name: string, icon: string) => Promise<{ success: boolean; error?: string } | void>;
}

export const ProfileEditModal: FC<ProfileEditModalProps> = ({ initialName, initialIcon, onClose, onSave }) => {
    const [name, setName] = useState(initialName);
    const [icon, setIcon] = useState(initialIcon);
    const [saving, setSaving] = useState(false);
    const [failure, setFailure] = useState<string | null>(null);
    const { uploadImage, uploading, error: uploadError } = useImageUpload();
    const { playSound, triggerCelebration } = useGamenics();

    const handleSubmit = async (e: any) => {
        e.preventDefault();
        if (!name.trim() || saving) return;
        playSound(GamenicsEvent.TAP_CONFIRM);
        setFailure(null);
        setSaving(true);
        const res = await onSave(name.trim(), icon);
        setSaving(false);
        // 戻り値が無い実装（従来型）は成功とみなす。失敗が返ったら閉じない
        if (res && res.success === false) {
            setFailure(res.error || '保存できませんでした。もう一度ためしてください。');
            return;
        }
        await triggerCelebration(GamenicsEvent.SUBMISSION_COMPLETE);
        onClose();
    };

    const handleFileChange = async (e: any) => {
        if (e.target.files && e.target.files[0]) {
            const result = await uploadImage(e.target.files[0]);
            if (result.success && result.data) setIcon(result.data.url);
        }
    };

    return (
        <AnimatePresence>
            <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="absolute inset-0 bg-ink/70 backdrop-blur-sm"
                    onClick={onClose}
                />

                <motion.div
                    initial={{ scale: 0.9, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    exit={{ scale: 0.9, opacity: 0 }}
                    role="dialog"
                    aria-modal="true"
                    aria-labelledby="profile-edit-title"
                    className="relative bg-cream border-2 border-ink rounded-pop-md shadow-pop p-6 w-full max-w-md"
                >
                    <h2 id="profile-edit-title" className="text-xl font-display font-black text-ink mb-5">
                        プロフィールを整える
                    </h2>

                    <form onSubmit={handleSubmit} className="flex flex-col gap-4">
                        <div>
                            <label htmlFor="profile-name" className="block text-xs font-bold text-ink-2 mb-1">
                                表示される名前
                            </label>
                            <input
                                id="profile-name"
                                type="text"
                                value={name}
                                onChange={e => { setName(e.target.value); if (failure) setFailure(null); }}
                                className="w-full min-h-[44px] bg-surface border-2 border-ink/20 rounded-pop-sm p-3 text-ink placeholder:text-ink-3 focus:border-primary outline-none transition-colors"
                                required
                                maxLength={30}
                            />
                            <p className="text-xs text-ink-2 mt-1">30文字まで。ほかの人に見える名前です。</p>
                        </div>

                        <div>
                            <span className="block text-xs font-bold text-ink-2 mb-1">アイコン画像</span>

                            <div className="flex items-center gap-4 mb-3">
                                <div className="w-16 h-16 rounded-full border-2 border-ink overflow-hidden bg-surface shrink-0">
                                    {icon
                                        ? <img src={icon} alt="いまのアイコン" className="w-full h-full object-cover" />
                                        : <div className="w-full h-full bg-cream-2" />}
                                </div>
                                <div className="flex-1">
                                    <label className="cursor-pointer btn btn-ghost min-h-[44px] w-full justify-center">
                                        {uploading ? COPY.uploading : COPY.uploadImage}
                                        <input
                                            type="file"
                                            className="hidden"
                                            accept="image/*"
                                            onChange={handleFileChange}
                                            disabled={uploading}
                                        />
                                    </label>
                                    <p className="text-[11px] text-ink-2 mt-1">正方形・5MBまでがきれいに出ます</p>
                                </div>
                            </div>

                            {uploadError && (
                                <p role="alert" className="text-pop-dark text-xs mb-2">{uploadError}</p>
                            )}

                            <details className="text-xs text-ink-2">
                                <summary className="cursor-pointer hover:text-ink min-h-[24px]">URLを直接入力する</summary>
                                <input
                                    type="text"
                                    value={icon}
                                    onChange={e => setIcon(e.target.value)}
                                    className="w-full min-h-[44px] bg-surface border-2 border-ink/20 rounded-pop-sm p-3 mt-2 text-ink placeholder:text-ink-3 focus:border-primary outline-none transition-colors"
                                    placeholder="https://... または /file/..."
                                />
                            </details>
                        </div>

                        {/* 失敗はモーダル内に出す。閉じてしまうと入力が消えてやり直せない */}
                        {failure && (
                            <p role="alert" aria-live="assertive" className="text-sm text-pop-dark bg-surface border-2 border-pop-dark rounded-pop-sm px-3 py-2">
                                {failure}
                            </p>
                        )}

                        <div className="flex gap-2 mt-2">
                            <button
                                type="button"
                                onClick={() => { playSound(GamenicsEvent.TAP_CANCEL); onClose(); }}
                                className="btn btn-ghost flex-1 min-h-[44px]"
                            >
                                {COPY.cancel}
                            </button>
                            <button
                                type="submit"
                                disabled={saving || uploading || !name.trim()}
                                className="btn btn-primary flex-1 min-h-[44px] disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                {saving ? COPY.saving : COPY.save}
                            </button>
                        </div>
                    </form>
                </motion.div>
            </div>
        </AnimatePresence>
    );
};
